/**
 * router/index.js
 * Vue Router configuration for ReArch Executive Reporting SPA.
 *
 * Routes:
 *   /login                              - Login page (unauthenticated entry)
 *   /dashboard                          - Exec dashboard (all active projects)
 *   /projects                           - Full project list
 *   /projects/:id                       - Project detail + report history
 *   /projects/:id/report/:period        - Report form (create/edit)
 *   /projects/:id/report/:period/view   - Read-only report view
 *   /admin                              - Admin: project toggles, lookups
 *   /admin/billing-forecast-lab         - Admin-only historical forecast prototype
 *
 * Navigation guards:
 *   - Unauthenticated users are redirected to /login.
 *   - /admin requires admin role; others redirect to /dashboard.
 *   - report edit/create route requires PM/admin role; readers/executives use
 *     read-only views.
 *   - Auth state is checked via the useAuth composable (loaded lazily to avoid
 *     circular imports at module init).
 */

import { createRouter, createWebHistory } from 'vue-router'

// Lazy-loaded views for better initial load performance.
const LoginView       = () => import('@/views/LoginView.vue')
const DashboardView   = () => import('@/views/DashboardView.vue')
const ProjectListView = () => import('@/views/ProjectListView.vue')
const ProjectDetailView = () => import('@/views/ProjectDetailView.vue')
const ReportFormView  = () => import('@/views/ReportFormView.vue')
const ReportView      = () => import('@/views/ReportView.vue')
const AdminView       = () => import('@/views/AdminView.vue')
const BillingForecastLabView = () => import('@/views/BillingForecastLabView.vue')

const routes = [
  {
    path: '/',
    redirect: '/dashboard',
  },
  {
    path: '/login',
    name: 'login',
    component: LoginView,
    meta: { public: true },
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: DashboardView,
    meta: { requiresAuth: true },
  },
  {
    path: '/projects',
    name: 'projects',
    component: ProjectListView,
    meta: { requiresAuth: true },
  },
  {
    path: '/projects/:id',
    name: 'project-detail',
    component: ProjectDetailView,
    meta: { requiresAuth: true },
  },
  {
    path: '/projects/:id/report/:period',
    name: 'report-form',
    component: ReportFormView,
    meta: { requiresAuth: true, requiresPm: true },
  },
  {
    path: '/projects/:id/report/:period/view',
    name: 'report-view',
    component: ReportView,
    meta: { requiresAuth: true },
  },
  {
    path: '/admin',
    name: 'admin',
    component: AdminView,
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/billing-forecast-lab',
    name: 'billing-forecast-lab',
    component: BillingForecastLabView,
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    // Catch-all: redirect unknown paths to dashboard.
    path: '/:pathMatch(.*)*',
    redirect: '/dashboard',
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    // Restore scroll position on back/forward; otherwise scroll to top.
    return savedPosition || { top: 0 }
  },
})

// ---- Navigation guard ----
router.beforeEach(async (to) => {
  // Public routes (e.g. /login) are always accessible.
  if (to.meta.public) return true

  // Dynamically import useAuth to avoid circular module dependencies.
  const { useAuth } = await import('@/composables/useAuth.js')
  const { user, isAdmin, isPm, loadUser } = useAuth()

  // Ensure user is loaded before checking auth state.
  if (!user.value) {
    await loadUser()
  }

  // Redirect unauthenticated users to login.
  if (to.meta.requiresAuth && !user.value) {
    return { name: 'login' }
  }

  // Redirect non-admin users away from admin routes.
  if (to.meta.requiresAdmin && !isAdmin.value) {
    return { name: 'dashboard' }
  }

  if (to.meta.requiresPm && !isPm.value) {
    return { name: 'project-detail', params: { id: to.params.id } }
  }

  return true
})

export default router
